import { IconCheck, IconList, IconPlus, IconX } from '@tabler/icons-react';
import { Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import type {
	CollectionItemField,
	FieldBindingCollection,
} from '@dealstack/shared/lib/documents/fieldBindingCatalogue.ts';
import {
	Chip,
	ChipGroup,
	ChipPlus,
	ChipRemove,
	ChipRow,
	CollectionSelect,
	DashedDivider,
	DataPointIconTile,
	DataPointRow,
	EyebrowLabel,
	ForEachRow,
	JoinWithControl,
	JoinWithRow,
	PreviewRow,
	ResolveToCardBox,
	RowText,
	ToggleButton,
} from './bindPanel.styled.ts';
import type { ListFieldSeparator, ListItemSeparator } from './BindPanel.types.ts';

interface Props {
	collections: readonly FieldBindingCollection[];
	collection: FieldBindingCollection;
	/** The rows offered to add. Chips label from `collection`, so a selected field stays removable. */
	offeredItemFields: readonly CollectionItemField[];
	selectedPaths: string[];
	fieldSeparator: ListFieldSeparator;
	itemSeparator: ListItemSeparator;
	preview: string | null;
	onChangeCollection: (key: string) => void;
	onToggleItemField: (path: string) => void;
	onRemoveItemField: (path: string) => void;
	onChangeFieldSeparator: (next: ListFieldSeparator) => void;
	onChangeItemSeparator: (next: ListItemSeparator) => void;
}

// The list ("for each") binding card: pick a collection, choose the item
// fields to print per item (ordered chips + toggleable rows, mirroring the
// scalar ResolveToCard/DataPointCatalogue pattern), choose how fields and items
// are separated, and preview the repeated sentence against the sample data.
export const ListBindingCard = ({
	collections,
	collection,
	offeredItemFields,
	selectedPaths,
	fieldSeparator,
	itemSeparator,
	preview,
	onChangeCollection,
	onToggleItemField,
	onRemoveItemField,
	onChangeFieldSeparator,
	onChangeItemSeparator,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const fieldByPath = new Map<string, CollectionItemField>(
		collection.itemFields.map((itemField) => [itemField.path, itemField])
	);
	const selectedFields = selectedPaths
		.map((path) => fieldByPath.get(path))
		.filter((itemField): itemField is CollectionItemField => itemField != null);

	const fieldSeparatorOptions: { value: ListFieldSeparator; label: string }[] = [
		{
			value: 'space',
			label: tMipManage('master_documents.field_binding.list.field_sep.space'),
		},
		{ value: 'of', label: tMipManage('master_documents.field_binding.list.field_sep.of') },
		{
			value: 'comma',
			label: tMipManage('master_documents.field_binding.list.field_sep.comma'),
		},
	];
	const itemSeparatorOptions: { value: ListItemSeparator; label: string }[] = [
		{ value: 'and', label: tMipManage('master_documents.field_binding.list.item_sep.and') },
		{ value: 'comma', label: tMipManage('master_documents.field_binding.list.item_sep.comma') },
		{
			value: 'newline',
			label: tMipManage('master_documents.field_binding.list.item_sep.newline'),
		},
	];

	const handleFieldSeparator = (value: string) => {
		if (value === 'space' || value === 'of' || value === 'comma') {
			onChangeFieldSeparator(value);
		}
	};
	const handleItemSeparator = (value: string) => {
		if (value === 'and' || value === 'comma' || value === 'newline') {
			onChangeItemSeparator(value);
		}
	};

	return (
		<ResolveToCardBox $variant={selectedFields.length > 0 ? 'selected' : 'empty'}>
			<EyebrowLabel>
				{tMipManage('master_documents.field_binding.list.for_each')}
			</EyebrowLabel>

			<ForEachRow>
				<Text size="xs" c="dimmed">
					{tMipManage('master_documents.field_binding.list.repeat_over')}
				</Text>
				<CollectionSelect
					size="sm"
					allowDeselect={false}
					aria-label={tMipManage('master_documents.field_binding.list.collection_aria')}
					value={collection.key}
					onChange={(value) => value && onChangeCollection(value)}
					data={collections.map((entry) => ({ value: entry.key, label: entry.label }))}
				/>
			</ForEachRow>

			{selectedFields.length > 0 ? (
				<ChipRow>
					{selectedFields.map((itemField, index) => (
						<ChipGroup key={itemField.path}>
							{index > 0 && <ChipPlus size={14} />}
							<Chip>
								<IconList size={12} />
								<Text size="xs" fw={500}>
									{itemField.label}
								</Text>
								<ChipRemove
									aria-label={tMipManage(
										'master_documents.field_binding.list.remove_field_aria',
										{ label: itemField.label }
									)}
									onClick={() => onRemoveItemField(itemField.path)}
								>
									<IconX size={10} />
								</ChipRemove>
							</Chip>
						</ChipGroup>
					))}
				</ChipRow>
			) : (
				<Text size="sm" c="dimmed">
					{tMipManage('master_documents.field_binding.list.empty')}
				</Text>
			)}

			<EyebrowLabel>
				{tMipManage('master_documents.field_binding.list.fields_per_item')}
			</EyebrowLabel>
			{offeredItemFields.map((itemField) => {
				const isSelected = selectedPaths.includes(itemField.path);
				return (
					<DataPointRow key={itemField.path}>
						<DataPointIconTile>
							<IconList size={16} />
						</DataPointIconTile>
						<RowText>
							<Text size="sm" fw={500}>
								{itemField.label}
							</Text>
						</RowText>
						<ToggleButton
							$selected={isSelected}
							aria-label={
								isSelected
									? tMipManage(
											'master_documents.field_binding.list.remove_aria',
											{ label: itemField.label }
										)
									: tMipManage('master_documents.field_binding.list.add_aria', {
											label: itemField.label,
										})
							}
							onClick={() => onToggleItemField(itemField.path)}
						>
							{isSelected ? <IconCheck size={14} /> : <IconPlus size={14} />}
						</ToggleButton>
					</DataPointRow>
				);
			})}

			<JoinWithRow>
				<Text size="xs" c="dimmed">
					{tMipManage('master_documents.field_binding.list.join_fields_with')}
				</Text>
				<JoinWithControl
					fullWidth
					size="sm"
					value={fieldSeparator}
					onChange={handleFieldSeparator}
					data={fieldSeparatorOptions}
				/>
			</JoinWithRow>

			<JoinWithRow>
				<Text size="xs" c="dimmed">
					{tMipManage('master_documents.field_binding.list.separate_items_with')}
				</Text>
				<JoinWithControl
					fullWidth
					size="sm"
					value={itemSeparator}
					onChange={handleItemSeparator}
					data={itemSeparatorOptions}
				/>
			</JoinWithRow>

			{preview !== null && (
				<>
					<DashedDivider />
					<PreviewRow>
						<Text size="xs" c="dimmed">
							{tMipManage('master_documents.field_binding.list.preview')}
						</Text>
						<Text size="sm" fw={700} data-testid="bind-panel-list-preview">
							{preview || '—'}
						</Text>
					</PreviewRow>
				</>
			)}
		</ResolveToCardBox>
	);
};
